import React, { Suspense } from 'react';

import { ProfileCompletionBanner } from 'features/auth';
import { Outlet, useLocation } from 'react-router-dom';
import { Footer } from 'widgets/footer';
import { AppHeader } from 'widgets/header';
import BottomTabBar from 'widgets/header/ui/BottomTabBar';

import CanonicalLink from '../shared/ui/CanonicalLink';
import Hreflang from '../shared/ui/Hreflang';
import LoadingSpinner from '../shared/ui/LoadingSpinner';

const loading = <LoadingSpinner />;

/**
 * @description
 * 헤더와 푸터가 포함된 레이아웃 컴포넌트입니다.
 * Suspense를 사용하여 lazy loading된 컴포넌트들의 로딩 상태를 처리합니다.
 *
 * routes.ts와 AppContent.tsx에서 많은 컴포넌트들이 React.lazy()로 lazy loading 되고 있음
 * 따라서 React.lazy()로 lazy loading된 컴포넌트들은 반드시 Suspense로 감싸야 함
 * 따라서 Suspense로 감싸주지 않으면 컴포넌트가 로드되기 전에 렌더링되려고 하기 때문에
 * "A component suspended while responding to synchronous input" 에러가 발생함
 *
 * @returns {JSX.Element} 헤더, 메인 콘텐츠, 푸터가 포함된 레이아웃
 */
function LayoutWithHeaderFooter() {
  const { pathname } = useLocation();

  const isFooterHidden = pathname.endsWith('/carts');
  // 비영어는 /ja/listing/123 같이 언어 prefix 가 붙으므로 prefix 제거 후 매칭
  // 지원 언어(vi/zh/ja)만 매칭 — 일반 라우트(/my 등) 가 잘못 strip 되는 것 방지
  const pathWithoutLang = pathname.replace(/^\/(?:vi|zh|ja)(?=\/|$)/, '');
  // 매물 상세 페이지(/listing/:id) — 하단에 AddCartSection 고정 CTA 가 이미 있어 탭바 중복 방지
  const isListingDetail = pathWithoutLang.startsWith('/listing/') && pathWithoutLang !== '/listing';
  // CartBottomBar 가 이미 하단에 있는 카트 플로우에서는 BottomTabBar 숨김 (중복 방지)
  const isBottomTabBarHidden = pathWithoutLang.startsWith('/carts') || isListingDetail;
  // Journey 는 폰에서 하단 탭바를 달고 앱처럼 쓰는 화면이라, 그 아래 마케팅 푸터가 붙으면
  // 개인 대시보드가 갑자기 홍보 페이지로 이어진다. 데스크톱은 폭 420px 한 컬럼이라
  // 푸터가 페이지를 마무리하는 역할을 해 그대로 둔다.
  const isJourney = pathWithoutLang === '/journey' || pathWithoutLang.startsWith('/journey/');

  return (
    <>
      <Hreflang />
      <CanonicalLink />
      {/* 모바일 매물 상세에서는 사진 위 floating 버튼이 헤더 역할을 함 — 데스크톱은 그대로 노출.
          비-listing-detail 페이지에선 wrapper 없이 AppHeader 직접 렌더 (sticky 가 정상 동작하려면
          containing block 이 충분히 커야 함 — wrapper div 로 감싸면 높이가 헤더와 동일해져 sticky 풀림) */}
      {isListingDetail ? (
        <div className="hidden md:contents">
          <AppHeader />
        </div>
      ) : (
        <AppHeader />
      )}
      {/* 폰(<800px)에서 BottomTabBar (h-16 = 64px) 가 fixed 라 footer 까지 가리지 않도록
          content + footer 를 함께 감싸 pb-16 적용 — 탭바 노출 조건(min-[800px]:hidden)과 동기.
          카트 페이지(탭바 숨김)는 padding 없음. */}
      <div className={!isBottomTabBarHidden ? 'pb-16 min-[800px]:pb-0' : ''}>
        <main className="flex min-h-screen min-w-full flex-col">
          <Suspense fallback={null}>
            <ProfileCompletionBanner />
          </Suspense>
          <Suspense fallback={loading}>
            <Outlet />
          </Suspense>
        </main>

        {!isFooterHidden && (
          <div className={isJourney ? 'hidden min-[800px]:block' : undefined}>
            <Footer />
          </div>
        )}
      </div>
      {!isBottomTabBarHidden && <BottomTabBar />}
    </>
  );
}

export default LayoutWithHeaderFooter;
